import { Component, Vue } from "vue-property-decorator";
import WithRender from "./Notifications330Page.html";
import { Inject } from "vue-di-container";
import ProviderService from "@/components/Provider/ProviderService";
import { DropdownModel } from "@/components/Provider/ProviderModel";
import { Toaster } from "@/Services/toast";
import { DatePicker } from "v-calendar";
import Notifications330Service from "./Notifications330Service";
import { Notifications330FilterModel } from "./Notifications330FilterModel";
import Notifications330KpiCards from "./components/Notifications330KpiCards";
import ComplaintsStatusesChart from "./components/ComplaintsStatusesChart";
import ComplaintFrequencyPerSenderChart from "./components/ComplaintFrequencyPerSenderChart";
import ComplaintsTimelineChart from "./components/ComplaintsTimelineChart";
import {
    createEmptyNotifications330StoredFilter,
    EMPTY_GUID,
    getTodayStr,
    loadNotifications330FilterFromStorage,
    toNotifications330FilterModel,
} from "./notifications330DetailsShared";

@WithRender
@Component({
    components: {
        Notifications330KpiCards,
        ComplaintsStatusesChart,
        ComplaintFrequencyPerSenderChart,
        ComplaintsTimelineChart,
        VcDatePicker: DatePicker,
    },
    diProvide: [ProviderService, Notifications330Service],
})
export default class Notifications330Page extends Vue {
    @Inject(ProviderService) public providerService!: ProviderService;
    @Inject(Notifications330Service)
    public notifications330Service!: Notifications330Service;

    public isReady: boolean = false;

    private readonly emptyGuid: string = EMPTY_GUID;

    public readonly monitorDashboardSelectMenuProps: Record<string, unknown> = {
        contentClass: "monitor-dashboard-select-menu",
        offsetY: false,
        maxHeight: 260,
    };

    public providers: DropdownModel[] = [];

    public operatorTenants: DropdownModel[] = [];

    public minDate: string = "";

    public createdDateRangeInternal: any = {
        start: "",
        end: "",
    };

    public createdDateModelConfig: any = {
        type: "string",
        mask: "YYYY-MM-DD",
    };

    public chartFilter: Notifications330FilterModel = {
        dateFrom: "",
        dateTo: "",
        tenant: this.emptyGuid,
        providerId: this.emptyGuid,
        customerType: 0,
    };

    public draftFilter: Notifications330FilterModel = {
        dateFrom: "",
        dateTo: "",
        tenant: this.emptyGuid,
        providerId: this.emptyGuid,
        customerType: 0,
    };

    private baselineFilter: Notifications330FilterModel = {
        dateFrom: "",
        dateTo: "",
        tenant: this.emptyGuid,
        providerId: this.emptyGuid,
        customerType: 0,
    };

    private reloadTimer: any = null;
    private reloadIntervalMs: number = 15 * 60 * 1000;
    private isAutoRefreshing: boolean = false;

    public get customerTypes(): DropdownModel[] {
        return [
            { id: 0, name: String(this.$t("monitorDashboard.all")) },
            { id: 1, name: String(this.$t("monitorDashboard.government")) },
            { id: 3, name: String(this.$t("monitorDashboard.private")) },
            { id: 2, name: String(this.$t("monitorDashboard.charity")) },
        ];
    }

    public get todayStr(): string {
        return getTodayStr();
    }

    public get dateRangeText(): string {
        const from =
            this.createdDateRangeInternal &&
                this.createdDateRangeInternal.start
                ? this.createdDateRangeInternal.start
                : this.draftFilter && this.draftFilter.dateFrom
                    ? this.draftFilter.dateFrom
                    : this.minDate || this.todayStr;

        const to =
            this.createdDateRangeInternal &&
                this.createdDateRangeInternal.end
                ? this.createdDateRangeInternal.end
                : this.draftFilter && this.draftFilter.dateTo
                    ? this.draftFilter.dateTo
                    : this.todayStr;

        return from + " - " + to;
    }

    public get isDraftDateRangeInvalid(): boolean {
        const from =
            this.createdDateRangeInternal &&
                this.createdDateRangeInternal.start
                ? this.createdDateRangeInternal.start
                : this.draftFilter.dateFrom;
        const to =
            this.createdDateRangeInternal &&
                this.createdDateRangeInternal.end
                ? this.createdDateRangeInternal.end
                : this.draftFilter.dateTo;
        if (!from || !to) {
            return false;
        }
        return String(from) > String(to);
    }

    private syncDraftDatesFromPicker(): void {
        const from =
            this.createdDateRangeInternal && this.createdDateRangeInternal.start
                ? this.createdDateRangeInternal.start
                : "";
        const to =
            this.createdDateRangeInternal && this.createdDateRangeInternal.end
                ? this.createdDateRangeInternal.end
                : "";

        if (from && !to) {
            this.draftFilter.dateFrom = from;
            this.draftFilter.dateTo = from;
            return;
        }

        if (from && to) {
            this.draftFilter.dateFrom = from;
            this.draftFilter.dateTo = to;
            return;
        }
    }

    private syncPickerFromDraft(): void {
        const from = this.draftFilter.dateFrom || this.minDate || this.todayStr;
        const to = this.draftFilter.dateTo || this.todayStr;

        this.createdDateRangeInternal = {
            start: from,
            end: to,
        };
    }

    private buildDefaultFilter(
        dateFrom: string,
        dateTo: string,
    ): Notifications330FilterModel {
        return {
            dateFrom,
            dateTo,
            tenant: this.emptyGuid,
            providerId: this.emptyGuid,
            customerType: 0,
        };
    }

    public async reload(): Promise<void> {
        this.isReady = false;

        const initialCalls = await Promise.all([
            this.providerService.GetProviderName(),
            this.notifications330Service.GetDefaultDates(),
            this.notifications330Service.GetOperatorTenants(),
            this.notifications330Service.GetMonitorDashboardOptions(),
        ]);

        const providerResponse = initialCalls[0];
        const providerResult =
            providerResponse && (providerResponse as any).result
                ? (providerResponse as any).result
                : [];
        this.providers = [
            { id: this.emptyGuid, name: String(this.$t("monitorDashboard.all")) },
        ].concat(providerResult.map((p: any) => ({ id: p.id, name: p.name })));

        const def = initialCalls[1];
        const dateFrom = def && def.dateFrom ? def.dateFrom : this.todayStr;
        const dateTo = def && def.dateTo ? def.dateTo : this.todayStr;
        this.minDate = dateFrom;

        const opTenantsRaw = initialCalls[2];
        const safeOpTenants = Array.isArray(opTenantsRaw) ? opTenantsRaw : [];
        this.operatorTenants = [
            { id: this.emptyGuid, name: String(this.$t("monitorDashboard.all")) },
        ].concat(safeOpTenants.map((t: any) => ({ id: t.id, name: t.name })));

        const fresh = this.buildDefaultFilter(dateFrom, dateTo);
        this.baselineFilter = { ...fresh };
        this.chartFilter = { ...fresh };
        this.draftFilter = { ...fresh };

        const stored = createEmptyNotifications330StoredFilter();
        stored.dateFrom = fresh.dateFrom;
        stored.dateTo = fresh.dateTo;
        stored.tenantId = fresh.tenant;
        stored.providerId = fresh.providerId;
        stored.customerType = fresh.customerType;
        loadNotifications330FilterFromStorage(stored);
        if (stored.dateFrom) {
            const restored = toNotifications330FilterModel(stored);
            this.chartFilter = { ...restored };
            this.draftFilter = { ...restored };
        }

        this.syncPickerFromDraft();

        const opts = initialCalls[3];
        const reloadInterval =
            opts && opts.reloadInterval ? Number(opts.reloadInterval) : 15;
        this.reloadIntervalMs = reloadInterval * 60 * 1000;
        this.startReloadTimer();

        this.isReady = true;

        await this.$nextTick();
        await this.refreshCharts();
    }

    public async applyFilters(): Promise<void> {
        this.syncDraftDatesFromPicker();
        if (this.isDraftDateRangeInvalid) {
            Toaster.error(String(this.$t("monitorDashboard.invalidDateRange")));
            return;
        }

        this.chartFilter = { ...this.draftFilter };

        await this.$nextTick();
        await this.refreshCharts();
    }

    public async resetFilters(): Promise<void> {
        this.draftFilter = { ...this.baselineFilter };
        this.syncPickerFromDraft();
        await this.applyFilters();
    }

    public async refresh(): Promise<void> {
        await this.refreshCharts();
    }

    private async refreshCharts(): Promise<void> {
        if (!this.isReady) return;

        const orderedRefs = [
            "kpiCards",
            "complaintsStatusesChart",
            "complaintFrequencyPerSenderChart",
            "complaintsTimelineChart",
        ];

        await Promise.all(orderedRefs.map((refName: string) => this.refreshChildByRef(refName)));
    }

    private async refreshChildByRef(refName: string): Promise<void> {
        const component: any = this.$refs[refName];
        if (component && component.refresh) {
            await component.refresh();
        }
    }

    public async mounted(): Promise<void> {
        await this.reload();
    }

    public created(): void {
        const today = this.todayStr;
        this.minDate = today;
        this.draftFilter.dateFrom = today;
        this.draftFilter.dateTo = today;
        this.chartFilter.dateFrom = today;
        this.chartFilter.dateTo = today;
        this.baselineFilter.dateFrom = today;
        this.baselineFilter.dateTo = today;
        this.createdDateRangeInternal = {
            start: today,
            end: today,
        };
    }

    public beforeDestroy(): void {
        this.clearReloadTimer();
    }

    private clearReloadTimer(): void {
        if (this.reloadTimer) {
            clearInterval(this.reloadTimer);
            this.reloadTimer = null;
        }
    }

    private startReloadTimer(): void {
        const self = this;
        this.clearReloadTimer();
        if (!this.reloadIntervalMs || this.reloadIntervalMs <= 0) return;

        this.reloadTimer = setInterval(async function () {
            if (!self.isReady) return;
            if (self.isAutoRefreshing) return;

            self.isAutoRefreshing = true;
            try {
                await self.refreshCharts();
            } finally {
                self.isAutoRefreshing = false;
            }
        }, this.reloadIntervalMs);
    }
}
